import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useApi, useInvalidateOn, type CaseSummary, type PagedResult } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation } from "@ncp/i18n";
import { DataTable, DeadlineKey, SourceText } from "@ncp/ui";
import { PeriodRing } from "../../components/PeriodRing";
import { RiskLevel } from "../../components/RiskLevel";
import { PROCESS_PAGES } from "../../pages/OtherRequestsPage";
import { stageOf } from "../case/process/phases";

/** Where the register of a case type lives in the section structure: the case workspace breadcrumb and old /cases links follow it. */
export function sectionOf(caseType: string): { href: string; labelKey: string } {
  if (caseType === "EconomicConcentration") return { href: "/concentration", labelKey: "nav.economicConcentration" };
  if (caseType === "Complaint" || caseType === "Whistleblowing") return { href: "/investigations", labelKey: "nav.investigationsComplaints" };
  if (caseType === "MarketStudy") return { href: "/data-bank", labelKey: "nav.dataBank" };
  return PROCESS_PAGES[caseType as keyof typeof PROCESS_PAGES] ?? { href: `/requests?type=${caseType}`, labelKey: "nav.exemptions" };
}

/** Types whose files carry a concentration risk score; other registers have no risk column (visual item 23). */
const RISK_TYPES = new Set(["EconomicConcentration"]);

/**
 * The register of one case type: every file of that type the officer can see, open or closed, in a stated order — open files with the
 * nearest statutory deadline first, then the rest by reference number, newest first. A file's stage uses the six words of its stage bar;
 * a closed file shows no running period and no risk.
 */
export function CaseRegisterTable({ type }: { type: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const navigate = useNavigate();
  const cases = useQuery({ queryKey: ["cases", type], queryFn: () => api.get<PagedResult<CaseSummary>>("/api/cases", { type, pageSize: 200 }) });
  useInvalidateOn(["case.updated", "proposal.created", "proposal.updated"], [["cases"]]);
  const withRisk = RISK_TYPES.has(type);

  const columns = useMemo<ColumnDef<CaseSummary, unknown>[]>(() => {
    const stage = (c: CaseSummary) => (c.decided ? "closure" : stageOf(c.type, c.state, c.status));
    const cols: ColumnDef<CaseSummary, unknown>[] = [
      { accessorKey: "title", header: t("backoffice:cases.columns.title"), meta: { className: "min-w-56" }, cell: ({ row }) => (
        <div className="min-w-0">
          <p className="line-clamp-2 font-medium"><SourceText text={locale === "ar" && row.original.titleAr ? row.original.titleAr : row.original.title} /></p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground"><bdi className="font-mono">{row.original.caseNumber}</bdi>{(locale === "ar" && row.original.organizationNameAr) || row.original.organizationName ? <> · {(locale === "ar" && row.original.organizationNameAr) || row.original.organizationName}</> : null}</p>
        </div>
      ) },
      { id: "stage", header: t("backoffice:cases.columns.stage"), meta: { className: "w-28 whitespace-nowrap" }, cell: ({ row }) => <span className="text-sm">{t(`backoffice:workspace.phases.${stage(row.original)}`)}</span> },
      { accessorKey: "assigneeName", header: t("backoffice:cases.columns.assignee"), meta: { className: "hidden w-44 lg:table-cell" }, cell: ({ row }) => row.original.assigneeName
        ? <span className="text-sm">{(locale === "ar" && row.original.assigneeNameAr) || row.original.assigneeName}</span>
        : row.original.responsibleName ? <span className="text-sm">{row.original.responsibleName}</span>
        : row.original.unattended ? <span className="text-xs font-medium text-danger-700">{t("backoffice:cases.unattended")}</span>
        : <span className="text-xs text-muted-foreground">{t("misc.unassigned")}</span> },
      { id: "clock", header: t("backoffice:cases.columns.period"), meta: { className: "w-44" }, cell: ({ row }) => row.original.primaryClock && stage(row.original) !== "closure"
        ? <PeriodRing clock={row.original.primaryClock} />
        : <span className="text-xs text-muted-foreground">{stage(row.original) === "closure" ? t(`status.${row.original.status}`, { defaultValue: row.original.status }) : t("backoffice:cases.noPeriod")}</span> },
      ...(withRisk ? [{ accessorKey: "riskScore", header: t("backoffice:cases.columns.risk"), meta: { className: "hidden w-24 sm:table-cell" }, cell: ({ row }: { row: { original: CaseSummary } }) => stage(row.original) === "closure" ? <span className="text-xs text-muted-foreground">—</span> : <RiskLevel score={row.original.riskScore} /> } as ColumnDef<CaseSummary, unknown>] : []),
      { accessorKey: "updatedAt", header: t("backoffice:cases.columns.lastChange"), meta: { className: "hidden w-32 xl:table-cell" }, cell: ({ row }) => <span className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(row.original.updatedAt, locale)}</span> },
    ];
    return cols;
  }, [t, locale, withRisk]);

  // Drafts are the applicant's until submitted; the register lists what reached the Department.
  const rows = useMemo(() => {
    const due = (c: CaseSummary) => (c.primaryClock && stageOf(c.type, c.state, c.status) !== "closure" ? c.primaryClock.dueAt : null);
    return (cases.data?.items ?? []).filter((c) => c.state !== "Draft").sort((a, b) => {
      const da = due(a), db = due(b);
      if (da && db) return da.localeCompare(db);
      if (da) return -1;
      if (db) return 1;
      return b.caseNumber.localeCompare(a.caseNumber);
    });
  }, [cases.data]);
  // The key to the period rings sits under every list that shows them (visual item 22).
  return (
    <div>
      <DataTable columns={columns} data={rows} loading={cases.isLoading} searchable searchPlaceholder={t("actions.search")} pageSize={15} onRowClick={(c) => navigate(`/cases/${c.id}`)} emptyState={t("misc.noResults")}
        toolbar={<span className="text-xs text-muted-foreground">{t("backoffice:cases.order")}</span>} />
      <DeadlineKey className="mt-3" />
    </div>
  );
}
